<template>
  <div>
    <FormQueriesHeader :title="`Query ID: ${shortQueryId}`" @back="back" class="header" />
    <div class="margin-from-top" v-if="canCraAccess || canDmAccess || canPiAccess || canSubPiAccess || canStudyCoordinatorAccess || canHomeHealthAccess || canPharmacovigilanceAccess || canCallCenterAccess || canCraUBAccess">
      <FormQueriesCommentForm :saveBtnText="'Post Reply'" v-if="replyQuery" :replyQuery="true" @submit="createComment" @cancel="cancelComment" />
      <b-row v-else>
        <b-col class="pr-1">
          <button-cmp v-if="!closed && !user.readOnly" id="reply-query-button" :class="isButtonDisabled ? 'disable-button' : ''" variant="outline-primary" @click="reply" class="mr-3">Reply</button-cmp>
          <button-cmp v-if="!closed && (canCraAccess || canDmAccess || canPharmacovigilanceAccess || canCallCenterAccess || canCraUBAccess) && !user.readOnly" id="close-query-button" :class="isButtonDisabled || isReopenCloseQueryButtonDisabled ? 'disable-button' : ''" variant="outline-primary" @click="close">Close Query </button-cmp>
          <button-cmp v-else-if="(canCraAccess || canDmAccess || canPharmacovigilanceAccess || canCallCenterAccess || canCraUBAccess) && !user.readOnly" id="reopen-query-button" :class="isButtonDisabled || isReopenCloseQueryButtonDisabled ? 'disable-button' : ''" variant="outline-primary" @click="reopen">Reopen Query</button-cmp>
          <b-popover v-if="isButtonDisabled" target="reply-query-button" triggers="hover" placement="bottomright"> You cannot reply to queries on locked activities. </b-popover>
          <b-popover v-if="isButtonDisabled" target="close-query-button" triggers="hover" placement="bottomleft"> You cannot close queries on locked activities.</b-popover>
          <b-popover v-if="isReopenCloseQueryButtonDisabled" target="close-query-button" triggers="hover" placement="bottomleft"> You cannot close queries that were <br />raised by a different role. </b-popover>
          <b-popover v-if="isReopenCloseQueryButtonDisabled" target="reopen-query-button" triggers="hover" placement="bottomleft"> You cannot reopen queries raised by a <br />different role. </b-popover>
          <b-popover v-if="isButtonDisabled" target="reopen-query-button" triggers="hover" placement="bottomleft"> You cannot reopen queries on locked activities. </b-popover>
        </b-col>
      </b-row>
    </div>
    <div :class="showReplyQuery() == true ? 'comment-section' : ''">
      <FormQueriesQueryComment class="margin-from-top" v-for="comment in comments" :name="getUserName(comment.createdBy)" :comment="comment" :key="comment.createdAt" />
    </div>
    <ConfirmationModal ref="closeConfirmation" text="Are you sure you want to close this query?" yesBtnTxt="Yes, close query" noBtnTxt="No, do not close query" @confirmed="closeConfirmed" />
    <ConfirmationModal ref="reopenConfirmation" text="Are you sure you want to reopen this query?" yesBtnTxt="Yes, reopen query" noBtnTxt="No, do not reopen query" @confirmed="reopenConfirmed" />
  </div>
</template>

<script>
import { AclRule } from "vue-acl";
import { mapGetters } from "vuex";
import FormQueriesQueryComment from "@/components/form/edc-form-queries/form-queries-query-comment";
import FormQueriesHeader from "@/components/form/form-queries-header";
import FormQueriesCommentForm from "@/components/form/edc-form-queries/form-queries-comment-form";
import ButtonCmp from "@/components/common/button";
import ConfirmationModal from "@/components/modals/confirmation-modal";

import { PI, CRA, DM, SUB_PI, STUDY_COORDINATOR, HOME_HEALTH, PHV, CC, CraUB } from "@/constants/authRoles";
import localStorageService from "@/services/localStorage";

export default {
  name: "form-queries-query-details",
  components: {
    FormQueriesQueryComment,
    FormQueriesHeader,
    FormQueriesCommentForm,
    ConfirmationModal,
    ButtonCmp
  },
  props: {
    query: Object,
    closed: Boolean,
    comments: Array,
    siteId: String,
    userNames: Object,
    lockStatus: String
  },
  data: () => ({
    replyQuery: null,
    issuerRole: null
  }),
  computed: {
    ...mapGetters(["user"]),
    userAccess() {
      return new AclRule(this.user.userRole).generate();
    },
    canPiAccess() {
      this.$acl.change(`${PI}`);
      return this.$acl.check("userAccess");
    },
    canCraAccess() {
      this.$acl.change(`${CRA}`);
      return this.$acl.check("userAccess");
    },
    canCraUBAccess() {
      this.$acl.change(`${CraUB}`);
      return this.$acl.check("userAccess");
    },
    canDmAccess() {
      this.$acl.change(`${DM}`);
      return this.$acl.check("userAccess");
    },
    canSubPiAccess() {
      this.$acl.change(`${SUB_PI}`);
      return this.$acl.check("userAccess");
    },
    canStudyCoordinatorAccess() {
      this.$acl.change(`${STUDY_COORDINATOR}`);
      return this.$acl.check("userAccess");
    },
    canPharmacovigilanceAccess() {
      this.$acl.change(`${PHV}`);
      return this.$acl.check("userAccess");
    },
    canHomeHealthAccess() {
      this.$acl.change(`${HOME_HEALTH}`);
      return this.$acl.check("userAccess");
    },
    canCallCenterAccess() {
      this.$acl.change(`${CC}`);
      return this.$acl.check("userAccess");
    },
    shortQueryId() {
      if (this.query) {
        return this.query.queryId.slice(0, 5).toUpperCase();
      }
      return "";
    },
    isButtonDisabled() {
      let userObj = localStorageService.getItem("userObj");
      const valuesToCheck = ["activity_response_query.close_locked", "activity_response_query.reopen_locked", "activity_response_query.reply_locked"];
      const restrictionsArray = userObj.restrictions.split(" ");
      const containsValue = valuesToCheck.some(value => restrictionsArray.includes(value));
      if (containsValue && this.lockStatus.toLowerCase() === "locked") {
        return true;
      }
      return false;
    },
    isReopenCloseQueryButtonDisabled() {
      let userObj = localStorageService.getItem("userObj");
      if (userObj.restrictions !== "" && this.lockStatus.toLowerCase() === "unlocked") {
        if(!["cra", "craub"].includes(this.issuerRole)) {
          return !!userObj.restrictions.includes("activity_response_query.close_assigned");
        }
      } else {
        return false;
      }
    }
  },
  watch: {
    comments: {
      handler(val){
        const arrayLength = val.length - 1;
        this.issuerRole = val[arrayLength].issuerRole.toLowerCase();
      }
    }
  },
  methods: {
    showReplyQuery() {
      if (this.replyQuery) {
        if (this.replyQuery.length) {
          return true;
        }
      } else {
        return false;
      }
    },
    back() {
      this.$emit("back");
    },
    reply() {
      if (this.isButtonDisabled) {
        return true;
      }
      this.replyQuery = this.query.queryId;
      return false;
    },
    reopen() {
      if (this.isButtonDisabled  || this.isReopenCloseQueryButtonDisabled) {
        return true;
      }
      this.$refs.reopenConfirmation.show();
      return false;
    },
    close() {
      if (this.isButtonDisabled || this.isReopenCloseQueryButtonDisabled) {
        return true;
      }
      this.$refs.closeConfirmation.show();
      return false;
    },
    cancelComment() {
      this.replyQuery = null;
    },
    createComment(commentData) {
      this.$emit("onQueryCommentCreate", {
        queryId: this.query.queryId,
        comment: commentData
      });
      this.replyQuery = null;
    },
    closeConfirmed() {
      this.$emit("onQueryClosed", this.query);
    },
    reopenConfirmed() {
      this.$emit("onQueryReopened", this.query);
    },
    getUserName(userId) {
      return this.userNames[userId];
    }
  }
};
</script>

<style scoped>
.margin-from-top {
  margin-top: 20px;
}
.comment-section {
  height: 315px;
  overflow: auto;
}
.disable-button {
  opacity: 0.3;
}
</style>
